import { FC } from "react";
import moment from "moment";
import {
  CalendarIcon,
  UserGroupIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";

interface QueryInfoSummaryProps {
  checkInDate: Date;
  checkOutDate: Date;
  numberOfMonths: number;
  numberOfGuests: number;
  isSharedProperty: boolean;
  bookedRoom?: string;
  propertyTitle: string;
  totalRooms?: number;
}

const QueryInfoSummary: FC<QueryInfoSummaryProps> = ({
  checkInDate,
  checkOutDate,
  numberOfMonths,
  numberOfGuests,
  isSharedProperty,
  bookedRoom,
  propertyTitle,
  totalRooms,
}) => {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-lg p-6 mt-6 border border-blue-200 dark:border-blue-800">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
        <HomeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        Query Details
      </h3>

      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <CalendarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Stay Period
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {moment(checkInDate).format("MMM DD, YYYY")} to{" "}
              {moment(checkOutDate).format("MMM DD, YYYY")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              {numberOfMonths} {numberOfMonths === 1 ? "month" : "months"}
            </p>
          </div>
        </div>

        <hr className="border-blue-200 dark:border-blue-800" />

        <div className="flex items-start gap-3">
          <UserGroupIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Number of Guests
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {numberOfGuests} {numberOfGuests === 1 ? "guest" : "guests"}
            </p>
          </div>
        </div>

        <hr className="border-blue-200 dark:border-blue-800" />

        <div className="flex items-start gap-3">
          <HomeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Property Type
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {isSharedProperty ? "Shared Property" : "Entire Property"}
            </p>
            {isSharedProperty && bookedRoom && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Room: {bookedRoom}
              </p>
            )}
            {!isSharedProperty && totalRooms && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                Total Rooms: {totalRooms}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-100 dark:bg-blue-900/30 rounded-md">
        <p className="text-xs text-blue-800 dark:text-blue-200">
          <span className="font-semibold">Note:</span> This is an inquiry. No
          payment is required until the booking is confirmed.
        </p>
      </div>
    </div>
  );
};

export default QueryInfoSummary;
